import { useEffect, useState, useRef } from 'react';
import clsx from 'clsx';
import tuvSrc from '@/assets/images/certificates/tuv.jpg';
import Button from '@/components/button';
import ButtonLink from '@/components/button-link';
import Logos from '@/components/logos';
import NumberedList from '@/components/numbered-list';
import SeePriceHead from '@/components/see-price-head';
import useWindowSize from '@/hooks/useWindowSize';
import { useAppSelector } from '@/redux/store';
import { ConfigSlice } from '@/redux/config';
import '@/modals/see-price/see-price-v2/see-price-v2.css';

export default function ModalSeePriceV2(props) {
  const {
    initialStep = 1,
    withRSV,
    onUpdateModifiers,
    onMoreClick,
    onButtonClick,
    onAlternativeButtonClick,
    onInterestInDDV,
  } = props;

  const certificateLogos = useAppSelector(ConfigSlice.selectors.certificateLogos);

  const [step, setStep] = useState(initialStep);
  const [headHeight, setHeadHeight] = useState(0);
  const [withRSVVersion, setWithRSVVersion] = useState(withRSV);
  const headRef = useRef(null);
  const topRef = useRef(null);
  const { width, height } = useWindowSize();

  const goToFirstStep = () => {
    setStep(1);
  };

  const goToSecondStep = () => {
    setStep(2);
    onMoreClick();
  };

  const goToAnotherVersion = (callInterest) => {
    if (callInterest) {
      onInterestInDDV();
    }
    setStep(1);
    setWithRSVVersion((withRSVVersion) => !withRSVVersion);
  };

  const updateModalModifiers = () => {
    if (width && width >= 768 && step === 1 && !withRSVVersion) {
      onUpdateModifiers && onUpdateModifiers({ add: ['see-price-v2', 'see-price-v2-large'] });
    }
    if (width && width >= 768 && step === 2) {
      onUpdateModifiers && onUpdateModifiers({ add: ['see-price-v2', 'see-price-v2-large'] });
    }
    if (width && width >= 768 && step === 1 && withRSVVersion) {
      onUpdateModifiers && onUpdateModifiers({ remove: ['see-price-v2-large'] });
    }
  };

  const getHeaderHeight = () => {
    if (height && width && headRef.current) {
      setHeadHeight(headRef.current?.getBoundingClientRect().height);
    }
  };

  const scrollToTop = () => {
    topRef.current && topRef.current.scrollIntoView({ block: 'center', behavior: 'smooth' });
  };

  function seePriceV2Content(withRSV, step) {
    return clsx('see-price-v2__content', {
      ['see-price-v2__content--first-step-with-rsv']: step === 1 && withRSV,
      ['see-price-v2__content--first-step-without-rsv']: step === 1 && !withRSV,
      ['see-price-v2__content--second-step']: step === 2,
    });
  }

  function seePriceV2Title(withRSV, step) {
    return clsx('see-price-v2__title', {
      ['see-price-v2__title--first-step-with-rsv']: step === 1 && withRSV,
      ['see-price-v2__title--second-step']: step === 2,
    });
  }

  function seePriceV2Navigation(withRSV, step) {
    return clsx('see-price-v2__navigation', {
      ['see-price-v2__navigation--first-step-with-rsv']: step === 1 && withRSV,
      ['see-price-v2__navigation--first-step-without-rsv']: step === 1 && !withRSV,
      ['see-price-v2__navigation--second-step']: step === 2,
    });
  }

  function seePriceV2Certificates(withRSV, step) {
    return clsx('see-price-v2__certificates', {
      ['see-price-v2__certificates--first-step-with-rsv']: step === 1 && withRSV,
    });
  }

  function seePriceV2Button(withRSV, step) {
    return clsx('see-price-v2__button', {
      ['see-price-v2__button--first-step-without-rsv']: step === 1 && !withRSV,
    });
  }

  useEffect(() => {
    onUpdateModifiers && onUpdateModifiers({ add: ['see-price-v2'] });
  }, []);

  useEffect(() => {
    if (initialStep !== step) {
      setStep(initialStep);
    }
  }, [initialStep]);

  useEffect(() => {
    updateModalModifiers();
  }, [step, width, withRSVVersion, initialStep]);

  useEffect(() => {
    getHeaderHeight();
    scrollToTop();
  });

  return (
    <div className='see-price-v2'>
      {withRSVVersion && step === 1 ? (
        <SeePriceHead ref={headRef} version='new-explicit' />
      ) : (
        <SeePriceHead ref={headRef} version='new-short' />
      )}
      <div
        className={seePriceV2Content(withRSVVersion, step)}
        style={{ maxHeight: `calc(${width && width < 768 ? 100 : 90}vh - ${headHeight}px)` }}
      >
        <div ref={topRef} />
        {withRSVVersion && step === 1 && (
          <>
            <h6 className={seePriceV2Title(withRSVVersion, step)}>So funktioniert’s</h6>
            <NumberedList>
              <>
                <strong>Sie klicken auf den unteren Button</strong>, um auf die Online Vollmacht zu gelangen.
              </>
              <>
                Bei der Kostenübersicht <strong>klicken Sie auf “Ich bin rechtschutzverischert”.</strong>
              </>
              <>
                <strong>Sie erteilen uns die Vollmacht,</strong> teilen uns Ihre Versicherungsdaten mit und wir stellen
                automatisch die Deckungsanfrage bei Ihrer Versicherung.
              </>
            </NumberedList>
            <div className={seePriceV2Button(withRSVVersion, step)}>
              <Button color='primary' iconPosition='after' icon='arrow' onClick={onButtonClick}>
                Vollmacht ansehen
              </Button>
            </div>
            <div className={seePriceV2Navigation(withRSVVersion, step)}>
              <ButtonLink onClick={() => goToAnotherVersion(false)}>Doch nicht rechtsschutzversichert?</ButtonLink>
            </div>
          </>
        )}
        {!withRSVVersion && step === 1 && (
          <>
            <p className='see-price-v2__explanation'>
              Die Kosten für den Einspruch können Sie entweder als Selbstzahler tragen oder{' '}
              <strong>von Ihrer Rechtsschutzversicherung übernehmen lassen</strong>.
            </p>
            <div className='see-price-v2__panels'>
              <div className='see-price-v2__panel see-price-v2__panels--alternative'>
                <div className='see-price-v2__board'>
                  <span>
                    <strong>Empfehlung:</strong> Selbstzahler
                  </span>
                </div>
                <div className='see-price-v2__wrapper'>
                  <div className='see-price-v2__paragraphs'>
                    <p className='see-price-v2__paragraph'>
                      <strong>Wir vertreten Sie als Privatzahler für pauschal 500 Euro inkl. Mwst.</strong> Damit sind
                      alle Kosten für das außergerichtliche Verfahren vollständig abgedeckt.
                    </p>
                    <p className='see-price-v2__paragraph'>
                      Sollte der Einspruch gegenüber der Behörde nicht erfolgreich sein und Sie die Sache vor Gericht
                      fortführen möchten, fallen weitere Gebühren an. Hierüber informieren wir Sie rechtzeitig und
                      helfen Ihnen bei der Entscheidung.
                    </p>
                  </div>
                  <div className={seePriceV2Button(withRSVVersion, step)}>
                    <Button color='primary' iconPosition='after' icon='arrow' onClick={onAlternativeButtonClick}>
                      Vollmacht ansehen
                    </Button>
                  </div>
                </div>
              </div>
              <div className='see-price-v2__panel see-price-v2__panels--recommendation'>
                <div className='see-price-v2__board'>
                  <span>
                    <strong>Alternative:</strong> Rückwirkende Rechtsschutzversicherung abschließen
                  </span>
                </div>
                <div className='see-price-v2__wrapper'>
                  <div className='see-price-v2__paragraphs'>
                    <p className='see-price-v2__paragraph'>
                      Wenn Sie eine rückwirkende Rechtsschutzversicherung abschließen,{' '}
                      <strong>rechnen wir unsere Leistung direkt mit Ihrer neuen Versicherung ab</strong>.
                    </p>
                  </div>
                  <div className={seePriceV2Button(withRSVVersion, step)}>
                    <Button full color='primary' iconPosition='after' icon='arrow' onClick={goToSecondStep}>
                      Mehr erfahren
                    </Button>
                  </div>
                  <div className={seePriceV2Navigation(withRSVVersion, step)}>
                    <ButtonLink onClick={() => goToAnotherVersion(false)}>Bereits rechtsschutzversichert?</ButtonLink>
                  </div>
                </div>
              </div>
            </div>
          </>
        )}
        {step === 2 && (
          <>
            <h6 className={seePriceV2Title(withRSVVersion, step)}>
              Kostendeckung über eine rückwirkende Rechtsschutzversicherung
            </h6>
            <div>
              <p className='see-price-v2__paragraph'>
                Es ist seit einigen Jahren möglich, auch für Fälle die bereits eingetreten sind, eine rückwirkende
                Rechtsschutzversicherung abzuschließen, die sofort den vollen Schutz bietet.
              </p>
              <p className='see-price-v2__paragraph'>
                Eine Möglichkeit in diese Richtung bietet die{' '}
                <strong>KS/AUXILIA mit dem Verkehrs-Rechtsschutz flex sofort</strong>.
              </p>
              <p className='see-price-v2__paragraph'>
                Der <strong>KS/AUXILIA Verkehrs-Rechtsschutz flex sofort</strong> ist ab{' '}
                <strong>monatliche 32,50 €</strong> abschließbar und deckt alle Anwalts- und Gerichtskosten - auch wenn
                der Ärger schon da ist, ohne Wartezeit.
              </p>
              <p className='see-price-v2__paragraph'>
                Klicken Sie unten wenn Sie mehr über diese Möglickkeit erfahren möchten bzw.{' '}
                <strong>einfach und schnell diese Versicherung gleich abschließen</strong> möchten.
              </p>
              <p className='see-price-v2__paragraph'>
                <u>WICHTIG!</u> Die Vollmacht sollten Sie uns in diesem Fall über den Link den wir soeben an Ihre
                E-Mailadresse gesendet haben,{' '}
                <strong>erst nachdem Sie den rückwirkenden Rechtsschutz abgeschlossen haben</strong>, erteilen. Sonst
                könnte die Versicherung die Kostenübernahme verweigern.
              </p>
            </div>
            <div className={seePriceV2Navigation(withRSVVersion, step)}>
              <Button
                href='https://beitrags-rechner.ks-auxilia.de/rightmart/?vmnr=121714'
                color='primary'
                iconPosition='after'
                icon='arrow'
                full
              >
                Zur KS/AUXILIA Versicherung
              </Button>
            </div>
            <div className={seePriceV2Navigation(withRSVVersion, step)}>
              <ButtonLink onClick={goToFirstStep}>Zurück</ButtonLink>
            </div>
          </>
        )}
        {step === 1 && (
          <div className={seePriceV2Certificates(withRSVVersion, step)}>
            <Logos show={certificateLogos} tuvSrc={tuvSrc} />
          </div>
        )}
      </div>
    </div>
  );
}

ModalSeePriceV2.displayName = 'ModalSeePriceV2';
